import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { registerCommunity } from '../services/onboardingService'
import { GOOGLE_CLIENT_ID, IOS_CLIENT_ID } from '../config'
import { detectPhoneRegion, getPhoneExample, getPhonePrefix, getPhoneValidationMessage, normalizeEmail, normalizePhone, validateAddress, validateBusinessName, validateEmail, validatePersonName, validatePhone } from '../utils/validation'

const STEPS = {
  DETAILS: 'details',
  DONE: 'done',
}

export default function CommunityRegistration() {
  const phoneRegion = detectPhoneRegion()
  const navigate = useNavigate()
  const location = useLocation()
  const [step, setStep] = useState(STEPS.DETAILS)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [idToken, setIdToken] = useState('')

  // Step 1 fields
  const [communityName, setCommunityName] = useState('')
  const [address, setAddress] = useState('')
  const [adminFirstName, setAdminFirstName] = useState('')
  const [adminLastName, setAdminLastName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminPhone, setAdminPhone] = useState(getPhonePrefix(phoneRegion))
  // Registering As is fixed: whoever registers a community is always its admin account (persisted as COMMUNITY_ADMIN).
  const [registrantUserType, setRegistrantUserType] = useState('COMMUNITY_ADMIN')
  // "Which best describes you?" drives registrantUserType: OWNER keeps the existing owner flow (unit number required),
  // PROPERTY_MANAGER maps to the same COMMUNITY_ADMIN value as before (default, no unit number required).
  const [registrantDescriptor, setRegistrantDescriptor] = useState('PROPERTY_MANAGER')
  const [unitNbr, setUnitNbr] = useState('')
  const [gatesInput, setGatesInput] = useState('Main Gate\nGate 1\nService Gate')

  const [communityId, setCommunityId] = useState('')

  // On mount: pick up the id_token from wherever it came from:
  //  - location.state (passed by OAuthCallback after web OAuth — most reliable)
  //  - URL hash (direct OAuth redirect to this page — legacy/fallback)
  //  - sessionStorage with community_reg_auth_complete flag (old flag-based handoff)
  useEffect(() => {
    try {
      // Case 1: Token passed directly via React Router navigation state (web flow via OAuthCallback)
      if (location.state?.fromOAuth && location.state?.idToken) {
        const token = location.state.idToken
        setIdToken(token)
        const parts = token.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
          const email = (payload.email || '').toLowerCase()
          if (email) setAdminEmail(email)
        }
        // Clear the nav state so a refresh doesn't re-use the old token
        window.history.replaceState({}, '', window.location.pathname)
        return
      }
      // Case 2: Web direct hash (legacy — keep as safety net)
      const hash = window.location.hash.replace(/^#/, '')
      if (hash) {
        const hashParams = new URLSearchParams(hash)
        const token = hashParams.get('id_token')
        const accessToken = hashParams.get('access_token')
        if (token) {
          window.history.replaceState(null, '', window.location.pathname)
          try { sessionStorage.setItem('id_token', token) } catch (e) {}
          if (accessToken) { try { sessionStorage.setItem('access_token', accessToken) } catch (e) {} }
          setIdToken(token)
          const parts = token.split('.')
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
            const email = (payload.email || '').toLowerCase()
            if (email) setAdminEmail(email)
          }
          return
        }
      }
      // Case 3: sessionStorage handoff flag (belt-and-suspenders fallback)
      const handoff = sessionStorage.getItem('community_reg_auth_complete')
      if (handoff) {
        sessionStorage.removeItem('community_reg_auth_complete')
        const stored = sessionStorage.getItem('id_token') || ''
        if (stored) {
          setIdToken(stored)
          const parts = stored.split('.')
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
            const email = (payload.email || '').toLowerCase()
            if (email) setAdminEmail(email)
          }
        }
      }
    } catch (e) { /* ignore */ }
  }, [])

  // Keep registrantUserType (the value actually submitted to the backend) in sync
  // with the "Which best describes you?" answer.
  useEffect(() => {
    const nextType = registrantDescriptor === 'OWNER' ? 'OWNER' : 'COMMUNITY_ADMIN'
    setRegistrantUserType(nextType)
    if (nextType !== 'OWNER') setUnitNbr('')
  }, [registrantDescriptor])

  // Sign in with Google to verify identity
  async function signInWithGoogle() {
    const isNative = typeof window !== 'undefined' && window.Capacitor &&
      (typeof window.Capacitor.isNativePlatform === 'function'
        ? window.Capacitor.isNativePlatform()
        : window.Capacitor.getPlatform?.() !== 'web')

    if (isNative) {
      // On native, use the native bridge (iOS AppDelegate / Android MainActivity).
      // This avoids Chrome Custom Tabs / WebView redirect issues with Google OAuth.
      setLoading(true)
      setError('')
      try {
        const { nativeSignIn } = await import('../nativeGoogleAuth')
        const res = await nativeSignIn({ clientId: IOS_CLIENT_ID })
        const token = res?.id_token || res?.idToken || null
        const nativeEmail = (res?._email || '').toLowerCase()
        if (!token) {
          setError('Authentication failed — no identity token received. Please try again.')
          setLoading(false)
          return
        }
        try { sessionStorage.setItem('id_token', token) } catch (e) {}
        setIdToken(token)
        if (nativeEmail) setAdminEmail(nativeEmail)
      } catch (err) {
        console.error('Native Google sign-in failed:', err)
        setError('Sign-in failed: ' + (err?.message || 'Please try again.'))
      }
      setLoading(false)
      return
    }

    // Web: route through the registered /oauth2/callback redirect URI.
    // Use localStorage (survives full-page redirects) to signal intent back to OAuthCallback.
    try { localStorage.setItem('oauth_intent', 'community_registration') } catch (e) {}
    const nonce = Math.random().toString(36).substring(2)
    const redirectUri = `${window.location.origin}/oauth2/callback`
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'token id_token',
      scope: 'openid email profile',
      nonce,
      prompt: 'select_account',
      state: 'community_registration',
    })
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
  }

  async function handleRegister(e) {
    e.preventDefault()
    setError('')
    if (!validateBusinessName(communityName)) {
      setError('Please enter a valid community name')
      return
    }
    if (!validateAddress(address)) {
      setError('Please enter a valid address')
      return
    }
    if (!validatePersonName(adminFirstName)) {
      setError('Please enter a valid admin first name')
      return
    }
    if (!validatePersonName(adminLastName)) {
      setError('Please enter a valid admin last name')
      return
    }
    if (!validateEmail(adminEmail)) {
      setError('Please enter a valid email address')
      return
    }
    if (!validatePhone(adminPhone, phoneRegion)) {
      setError(getPhoneValidationMessage(phoneRegion))
      return
    }
    if (registrantUserType === 'OWNER' && !unitNbr.trim()) {
      setError('Apartment / Unit Number is required when registering as Owner')
      return
    }
    setLoading(true)
    try {
      const result = await registerCommunity({
        communityName: communityName.trim(),
        address: address.trim(),
        adminFirstName: adminFirstName.trim(),
        adminLastName: adminLastName.trim(),
        adminEmail: normalizeEmail(adminEmail),
        adminPhone: normalizePhone(adminPhone, phoneRegion),
        userType: registrantUserType,
        unitNbr: unitNbr.trim(),
        gates: gatesInput.split(/\n|,|;/).map((item) => item.trim()).filter(Boolean),
      }, idToken)
      setCommunityId(result.communityId)
      setStep(STEPS.DONE)
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // handleVerifyOTP removed — OTP step skipped for MVP; Google sign-in is sufficient
  async function _unused_handleVerifyOTP(e) {
    e.preventDefault()
    setLoading(true)
    try {
      setStep(STEPS.DONE)
    } catch (err) {
      setError(err.message || 'Verification failed. Please check the code and try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── Gate: must be signed in with Google before filling the form ──
  if (!idToken) {
    return (
      <div style={styles.root}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.logoCircle}>
              <img src="/apartment-icon.svg" alt="Smart Community" width={36} height={36} />
            </div>
            <h1 style={styles.brand}>Smart Community</h1>
            <p style={styles.tagline}>Register Your Community</p>
          </div>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>
              Sign in with Google to verify your identity before registering.
            </p>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 24 }}>
              Your Gmail address will be used as the community admin email.
            </p>
            <button style={styles.btn} onClick={signInWithGoogle}>
              Sign in with Google to Continue
            </button>
            <p style={{ ...styles.backLink, marginTop: 16 }}>
              Already registered?{' '}
              <span style={styles.link} onClick={() => navigate('/')}>Sign In</span>
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logoCircle}>
            <img src="/apartment-icon.svg" alt="Smart Community" width={36} height={36} />
          </div>
          <h1 style={styles.brand}>Smart Community</h1>
          <p style={styles.tagline}>Register Your Community</p>
        </div>

        {/* Step indicator */}
        <div style={styles.stepBar}>
          <StepDot label="Details" active={step === STEPS.DETAILS} done={step === STEPS.DONE} />
          <div style={styles.stepLine} />
          <StepDot label="Pending Approval" active={step === STEPS.DONE} done={false} />
        </div>

        {/* ── Step 1: Community details ── */}
        {step === STEPS.DETAILS && (
          <form onSubmit={handleRegister} style={styles.form}>
            <h2 style={styles.sectionTitle}>Community Details</h2>

            <label style={styles.label}>Community Name *</label>
            <input
              style={styles.input}
              placeholder="e.g. Sunrise Apartments"
              value={communityName}
              onChange={e => setCommunityName(e.target.value)}
              autoComplete="organization"
              required
            />

            <label style={styles.label}>Address *</label>
            <input
              style={styles.input}
              placeholder="Full address of the community"
              value={address}
              onChange={e => setAddress(e.target.value)}
              autoComplete="street-address"
              required
            />

            <h2 style={{ ...styles.sectionTitle, marginTop: 20 }}>Admin Details</h2>

            <div style={styles.row}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>First Name *</label>
                <input
                  style={styles.input}
                  placeholder="First name"
                  value={adminFirstName}
                  onChange={e => setAdminFirstName(e.target.value)}
                  autoComplete="given-name"
                  required
                />
              </div>
              <div style={{ width: 12 }} />
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Last Name *</label>
                <input
                  style={styles.input}
                  placeholder="Last name"
                  value={adminLastName}
                  onChange={e => setAdminLastName(e.target.value)}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>

            <label style={styles.label}>Admin Email</label>
            <input
              style={{ ...styles.input, background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }}
              type="email"
              value={adminEmail}
              readOnly
              autoComplete="email"
            />
            <p style={styles.hint}>Your Google-verified email will be used as the admin email.</p>

            <label style={styles.label}>Admin Phone Number *</label>
            <input
              style={styles.input}
              type="tel"
              value={adminPhone}
              onChange={e => setAdminPhone(e.target.value)}
              placeholder={getPhoneExample(phoneRegion)}
              inputMode="tel"
              autoComplete="tel"
              required
            />
            <p style={styles.hint}>Phone format defaults to your app region. Example: {getPhoneExample(phoneRegion)}</p>

            <label style={styles.label}>Registering As</label>
            <input
              style={{ ...styles.input, background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }}
              value="Community Admin"
              readOnly
              disabled
            />
            <p style={styles.hint}>Community registration is always performed by the admin account.</p>

            <label style={styles.label}>Which best describes you? *</label>
            <select
              style={styles.input}
              value={registrantDescriptor}
              onChange={e => setRegistrantDescriptor(e.target.value)}
              required
            >
              <option value="PROPERTY_MANAGER">Property Manager</option>
              <option value="OWNER">Owner</option>
            </select>

            {registrantUserType === 'OWNER' && (
              <>
                <label style={styles.label}>Apartment / Unit Number *</label>
                <input
                  style={styles.input}
                  placeholder="e.g. A-101"
                  value={unitNbr}
                  onChange={e => setUnitNbr(e.target.value)}
                  autoComplete="off"
                  required
                />
              </>
            )}

            <label style={styles.label}>Entry / Exit Gates</label>
            <textarea
              style={{ ...styles.input, minHeight: 90, resize: 'vertical' }}
              placeholder="Main Gate
Gate 1
Service Gate"
              value={gatesInput}
              onChange={e => setGatesInput(e.target.value)}
            />
            <p style={styles.hint}>Enter one gate per line or separate with commas.</p>

            {error && <p style={styles.error}>{error}</p>}

            <button type="submit" style={styles.btn} disabled={loading}>
              {loading ? 'Submitting…' : 'Submit Registration →'}
            </button>

            <p style={styles.backLink}>
              Already registered?{' '}
              <span style={styles.link} onClick={() => navigate('/')}>Sign In</span>
            </p>
          </form>
        )}

        {/* ── Step 2: Done (pending approval) ── */}
        {step === STEPS.DONE && (
          <div style={styles.form}>
            <div style={{ ...styles.successIcon, background: '#fef9c3', color: '#854d0e' }}>⏳</div>
            <h2 style={styles.sectionTitle}>Registration Submitted!</h2>
            <p style={styles.hint}>
              <strong>{communityName}</strong> has been submitted and is now <strong>pending approval</strong>.
            </p>
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#78350f' }}>
              <strong>What happens next?</strong><br />
              An administrator will review your registration. You will receive an email at <strong>{adminEmail}</strong> once your community is approved. Only then will you be able to sign in to the management portal.
            </div>
            <button style={{ ...styles.btn, background: '#6b7280' }} onClick={() => navigate('/')}>
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function StepDot({ label, active, done }) {
  const bg = done ? '#16a34a' : active ? '#1d4ed8' : '#d1d5db'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%', background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontWeight: 'bold', fontSize: 13,
      }}>
        {done ? '✓' : active ? '●' : '○'}
      </div>
      <span style={{ fontSize: 11, color: active ? '#1d4ed8' : '#6b7280', fontWeight: active ? 600 : 400 }}>{label}</span>
    </div>
  )
}

const styles = {
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #3b82f6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '32px 28px',
    width: '100%',
    maxWidth: 480,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  header: { textAlign: 'center', marginBottom: 24 },
  logoCircle: {
    width: 64, height: 64, borderRadius: '50%',
    background: '#eff6ff', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 12px',
  },
  brand: { fontSize: 22, fontWeight: 700, color: '#1d4ed8', margin: 0 },
  tagline: { fontSize: 14, color: '#6b7280', margin: '4px 0 0' },
  stepBar: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    marginBottom: 28, gap: 0,
  },
  stepLine: { flex: 1, height: 2, background: '#e5e7eb', marginTop: 13, maxWidth: 60 },
  form: { display: 'flex', flexDirection: 'column', gap: 8 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#111827', margin: '4px 0 8px' },
  label: { fontSize: 13, fontWeight: 500, color: '#374151' },
  input: {
    padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box',
  },
  row: { display: 'flex', gap: 0 },
  hint: { fontSize: 12, color: '#6b7280', margin: '2px 0 4px', lineHeight: 1.4 },
  error: { fontSize: 13, color: '#dc2626', background: '#fef2f2', padding: '8px 12px', borderRadius: 6, margin: '4px 0' },
  btn: {
    marginTop: 8, padding: '12px 16px', background: '#1d4ed8', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
    cursor: 'pointer', width: '100%',
  },
  btnSecondary: {
    padding: '10px 16px', background: '#f3f4f6', color: '#374151',
    border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14,
    cursor: 'pointer', width: '100%',
  },
  backLink: { textAlign: 'center', fontSize: 13, color: '#6b7280', marginTop: 8 },
  link: { color: '#1d4ed8', cursor: 'pointer', fontWeight: 500 },
  successIcon: {
    fontSize: 48, textAlign: 'center', background: '#dcfce7',
    color: '#16a34a', width: 72, height: 72, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 16px',
  },
}
